import * as React from "react";
import { DayPicker } from "react-day-picker";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";

import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
    return (
        <DayPicker
            showOutsideDays={ showOutsideDays }
            className={ cn("p-3 select-none", className) }
            classNames={{
                month: "space-y-4",
                month_caption: "flex justify-center pt-1 relative items-center",
                caption_label: "text-sm font-medium",
                nav: "relative",
                button_previous: cn(
                    buttonVariants({ variant: "outline" }),
                    "h-7 w-7 z-10 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-1",
                ),
                button_next: cn(
                    buttonVariants({ variant: "outline" }),
                    "h-7 w-7 z-10 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-1",
                ),
                month_grid: "w-full border-collapse space-y-1",
                weekdays: "",
                weekday: "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
                row: "flex w-full mt-2",
                day: cn(
                    "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 rounded-md [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md",
                    props.mode === "range"
                        ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
                        : "[&:has([aria-selected])]:rounded-md",
                ),
                day_button: cn(buttonVariants({ variant: "ghost" }), "h-8 w-8 p-0 font-normal aria-selected:opacity-100"),
                range_start: "day-range-start",
                range_end: "day-range-end",
                selected:
                    "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                today: "bg-accent text-accent-foreground",
                outside: "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
                disabled: "text-muted-foreground opacity-50",
                range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                hidden: "invisible",
                ...classNames,
            }}
            components={{
                // eslint-disable-next-line no-shadow
                Chevron: props => {
                    if (props.orientation === "left") {
                        return <LuChevronLeft className={ cn("h-4 w-4", props.className) } { ...props } />;
                    }
                    return <LuChevronRight className={ cn("h-4 w-4", props.className) } { ...props } />;
                },
            }}
            { ...props }
        />
    );
}
Calendar.displayName = "Calendar";

export { Calendar };
